/**
 * EditState + decoded source → canvas. The only place pixels are touched.
 *
 * ONE transform, ONE resample (owner, 2026-09-06: "we cannot compromise on quality"). Every op —
 * crop, resize, rotate, flip — is an affine map, so the whole ordered list folds into a single
 * source→output matrix. The output is drawn from the ORIGINAL pixels in one drawImage: crops are clip
 * paths in their own space, corner fills are painted behind, flips and quarter turns are exact. When
 * the reduction is large the source is first downscaled ONCE with the browser's high-quality
 * resampler — to exactly the final scale when nothing rotates (so the final draw is 1:1), else to at
 * most 2× the final scale so the bilinear rotate never aliases.
 *
 * Previews run the same path at the SCREEN's resolution for the current zoom (device pixels), so a
 * photo shrunk to 277 px still previews sharply from the original instead of being a 100 px canvas
 * stretched across a Retina screen. Pre-downscaled copies live in a per-photo MipCache.
 */
import { EditState, Size, Op, cropRectPx, isIdentity, walk } from './edit-stack';
import { estimateOutputBytes } from './estimate';

export type Source = HTMLCanvasElement | ImageBitmap | HTMLImageElement;
/** Downscaled copies of a source keyed by their scale (1/2, 1/4, … or an exact export scale). */
export type MipCache = Map<number, Source>;

const OFFSCREEN = typeof OffscreenCanvas !== 'undefined';

export function makeCanvas(size: Size): HTMLCanvasElement {
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(size.width)); c.height = Math.max(1, Math.round(size.height));
    return c;
}

function ctx2d(c: HTMLCanvasElement): CanvasRenderingContext2D {
    const ctx = c.getContext('2d', { alpha: true });
    if (!ctx) throw new Error('This browser could not create a drawing surface for the image.');
    ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
    return ctx;
}

const dims = (src: Source): Size => ({ width: (src as any).naturalWidth || src.width, height: (src as any).naturalHeight || src.height });

/** Stepwise high-quality scale of a source to an exact size (thumbnails; the fallback resampler). */
export async function scaleTo(src: Source, target: Size): Promise<HTMLCanvasElement> {
    const sw = (src as any).naturalWidth || src.width, sh = (src as any).naturalHeight || src.height;
    if (sw === target.width && sh === target.height) { const c = makeCanvas(target); ctx2d(c).drawImage(src as any, 0, 0); return c; }
    if (typeof createImageBitmap === 'function' && target.width < sw) {
        try {
            const bmp = await createImageBitmap(src as any, { resizeWidth: target.width, resizeHeight: target.height, resizeQuality: 'high' });
            const c = makeCanvas(target); ctx2d(c).drawImage(bmp, 0, 0); bmp.close?.(); return c;
        } catch { /* fall through to stepwise */ }
    }
    let cur: Source = src, cw = sw, ch = sh;
    while (cw / 2 >= target.width && ch / 2 >= target.height) {
        const half = makeCanvas({ width: Math.round(cw / 2), height: Math.round(ch / 2) });
        ctx2d(half).drawImage(cur as any, 0, 0, half.width, half.height);
        cur = half; cw = half.width; ch = half.height;
    }
    const out = makeCanvas(target); ctx2d(out).drawImage(cur as any, 0, 0, target.width, target.height);
    return out;
}

// ---------------------------------------------------------------- affine maps (canvas order: x' = a·x + c·y + e, y' = b·x + d·y + f)

export type Affine = [number, number, number, number, number, number];
const ID: Affine = [1, 0, 0, 1, 0, 0];
/** A ∘ B: apply B first, then A. */
export function mul(A: Affine, B: Affine): Affine {
    return [A[0] * B[0] + A[2] * B[1], A[1] * B[0] + A[3] * B[1], A[0] * B[2] + A[2] * B[3], A[1] * B[2] + A[3] * B[3], A[0] * B[4] + A[2] * B[5] + A[4], A[1] * B[4] + A[3] * B[5] + A[5]];
}
const translate = (x: number, y: number): Affine => [1, 0, 0, 1, x, y];
const scaleM = (x: number, y = x): Affine => [x, 0, 0, y, 0, 0];
const rotateM = (rad: number): Affine => [Math.cos(rad), Math.sin(rad), -Math.sin(rad), Math.cos(rad), 0, 0];
export function apply(m: Affine, x: number, y: number): [number, number] { return [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]]; }

/** One op as an affine map from its input pixels to its output pixels. */
function opAffine(op: Op, inPx: Size, outPx: Size): Affine {
    switch (op.type) {
        case 'crop': { const r = cropRectPx(op, inPx); const t = translate(-r.x, -r.y); return op.platform ? mul(scaleM(outPx.width / r.width, outPx.height / r.height), t) : t; }
        case 'resize': return scaleM(outPx.width / inPx.width, outPx.height / inPx.height);
        case 'rotate': return mul(translate(outPx.width / 2, outPx.height / 2), mul(rotateM((op.quarterTurns * 90 + op.fineAngle) * Math.PI / 180), translate(-inPx.width / 2, -inPx.height / 2)));
        case 'flip': return mul(translate(op.h ? inPx.width : 0, op.v ? inPx.height : 0), scaleM(op.h ? -1 : 1, op.v ? -1 : 1));
    }
}

export interface Step { op: Op; inPx: Size; outPx: Size; post: Affine; }   // post: this op's OUTPUT space → the final output
export interface Composite { m: Affine; out: Size; steps: Step[]; rotates: boolean; }

/** The first `upTo` ops folded into one source→output matrix, plus each step's post-transform (clips, fills). */
export function composite(s: EditState, upTo = s.ops.length): Composite {
    const locals: Affine[] = []; const steps: Step[] = []; let inPx: Size = { ...s.source }; let rotates = false;
    // sizes come from the model's walk (cumulative-scale aware), so the transform lands on the model's numbers
    for (const st of walk(s, upTo)) {
        const { op, outPx } = st;
        if (!isIdentity(op)) {
            locals.push(opAffine(op, st.inPx, outPx)); steps.push({ op, inPx: st.inPx, outPx, post: ID });
            if (op.type === 'rotate' && op.fineAngle !== 0) rotates = true;
        }
        inPx = outPx;
    }
    let m = ID; for (const l of locals) m = mul(l, m);
    let post: Affine = ID;
    for (let i = steps.length - 1; i >= 0; i--) { steps[i].post = post; post = mul(post, locals[i]); }
    return { m, out: inPx, steps, rotates };
}

/** A copy of the source at scale `p` (≤ 1), built with the browser's high-quality resampler and cached. */
async function level(src: Source, p: number, mips?: MipCache): Promise<Source> {
    if (p >= 1) return src;
    // verify round item 7: a cached copy the memory trim let go (width 0) is rebuilt, never drawn
    const hit = mips?.get(p); if (hit && dims(hit).width > 0 && dims(hit).height > 0) return hit;
    if (hit) mips!.delete(p);
    const d = dims(src); const target = { width: Math.max(1, Math.round(d.width * p)), height: Math.max(1, Math.round(d.height * p)) };
    let out: Source | null = null;
    if (typeof createImageBitmap === 'function') { try { out = await createImageBitmap(src as any, { resizeWidth: target.width, resizeHeight: target.height, resizeQuality: 'high' }); } catch { out = null; } }
    if (!out) out = await scaleTo(src, target);
    mips?.set(p, out); return out;
}

export interface RenderOptions {
    scale?: number;      // canvas px per output px (1 = export; previews pass zoom × devicePixelRatio)
    maxEdge?: number;    // cap on the canvas's longer edge
    upTo?: number;       // render only the first n ops (the Crop tool's base)
    mips?: MipCache;     // per-photo cache of downscaled copies
    exact?: boolean;     // export: pre-scale to exactly the final scale (one true resample) instead of a cached power of two
    /** `src` is a downscaled copy of the photo (a strip card drawn from the file's own ≤128 px thumb once its pixels
     *  were let go, model/thumbs): the size its pixels stand for, i.e. what `s.source` names. Default: `src`'s own
     *  size (the original). Never pass `mips` with it: a level of the copy is not a level of the photo. */
    sourceSize?: Size;
}

/** The composed edit, drawn from the original in one pass. */
export async function render(src: Source, s: EditState, o: RenderOptions = {}): Promise<HTMLCanvasElement> {
    const C = composite(s, o.upTo);
    let k = o.scale ?? 1;
    if (o.maxEdge) k = Math.min(k, o.maxEdge / Math.max(C.out.width, C.out.height, 1));
    const lin = Math.sqrt(Math.abs(C.m[0] * C.m[3] - C.m[1] * C.m[2]) || 1);   // linear scale source → output
    // previews never draw more pixels than the source has; EXPORT does (owner ruling §12, 2026-09-07: upscaling
    // is simply on — the 2026-09-06 clamp here also hit export and silently saved every upscale at the
    // source's size, fix-all 2026-10-06 firsttime-1 / geometry-1 / geometry-2 / power-1 / regression-1)
    if (!o.exact && lin * k > 1) k = 1 / lin;
    const s2 = lin * k;                                                          // linear scale source → canvas
    const base = o.sourceSize ?? dims(src);                                      // the pixels s.source names
    const have = dims(src).width / base.width;                                   // src holds them at this scale (1 = the original)
    const need = s2 / have;                                                      // linear scale src → canvas
    // pre-downscale: exactly `need` when nothing rotates (export) → the final draw is 1:1; else the largest
    // power of two that leaves the final reduction ≤ 2× (cached across zooms)
    const p = need >= 0.999 ? 1 : (o.exact && !C.rotates) ? need : Math.max(1 / 64, Math.pow(2, Math.floor(Math.log2(Math.min(1, 2 * need)))));
    const img = await level(src, p, o.mips);
    const pReal = dims(img).width / base.width;
    const canvas = makeCanvas({ width: Math.round(C.out.width * k), height: Math.round(C.out.height * k) });
    const ctx = ctx2d(canvas);
    const K = scaleM(k);
    const set = (T: Affine) => ctx.setTransform(T[0], T[1], T[2], T[3], T[4], T[5]);
    // every crop is a window in its own output space (a disc for circle avatars); clips intersect
    for (const st of C.steps) if (st.op.type === 'crop') {
        set(mul(K, st.post)); ctx.beginPath();
        if (st.op.shape === 'circle') ctx.ellipse(st.outPx.width / 2, st.outPx.height / 2, st.outPx.width / 2, st.outPx.height / 2, 0, 0, Math.PI * 2);
        else ctx.rect(0, 0, st.outPx.width, st.outPx.height);
        ctx.clip();
    }
    // corner fills behind a fine-angle rotation that kept its bounds
    for (const st of C.steps) if (st.op.type === 'rotate' && st.op.fineAngle !== 0 && st.op.cornerFill !== 'transparent' && st.op.cornerFill !== 'auto-crop') {
        set(mul(K, st.post)); ctx.fillStyle = st.op.cornerFill; ctx.fillRect(0, 0, st.outPx.width, st.outPx.height);
    }
    // the photo, once: level px → source px (÷pReal) → output px (C.m) → canvas px (×k)
    set(mul(K, mul(C.m, scaleM(1 / pReal))));
    ctx.drawImage(img as any, 0, 0);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    return canvas;
}

/** Full-size output for export: one high-quality resample from the original. */
export async function renderOutput(src: Source, s: EditState, mips?: MipCache): Promise<HTMLCanvasElement> { return render(src, s, { scale: 1, mips, exact: true }); }

/**
 * Preview at the screen's resolution: `displayScale` = zoom × devicePixelRatio (device px per image
 * px), capped by `maxEdge` and by the source's own resolution. `upTo` renders only the first ops —
 * the Crop tool edits the image the ops before it produced.
 */
export async function renderPreview(src: Source, s: EditState, o: { maxEdge: number; displayScale?: number; upTo?: number; mips?: MipCache }): Promise<{ canvas: HTMLCanvasElement; scale: number }> {
    const canvas = await render(src, s, { scale: o.displayScale ?? 1, maxEdge: o.maxEdge, upTo: o.upTo, mips: o.mips });
    const out = composite(s, o.upTo).out;
    return { canvas, scale: canvas.width / Math.max(1, out.width) };
}

/** Byte estimate without a source to calibrate on — kept for old call sites; see model/estimate.ts (C1). */
export function estimateBytes(size: Size, format: string, quality = 85, hasAlpha = false): number {
    return estimateOutputBytes({ size, format, quality, hasAlpha });
}

/** Decimal units (ruling 7, J1-7 usage QA 2026-10-07): 1 MB = 1,000,000 B, as the OS shows the file — the
 *  binary 1024s read 6.2 MB for a 6.5 MB download. */
export function formatBytes(n: number): string {
    const v = Math.max(0, Math.round(n || 0));
    if (v < 1000) return `${v} B`;
    if (v < 999_500) return `${Math.round(v / 1000)} KB`;
    if (v < 999_500_000) { const mb = v / 1e6; return `${mb < 9.95 ? mb.toFixed(1) : Math.round(mb)} MB`; }
    const gb = v / 1e9; return `${gb < 9.95 ? gb.toFixed(1) : Math.round(gb)} GB`;
}

export function isOffscreenSupported(): boolean { return OFFSCREEN; }
